/**
 * N2ResearchLetterPage — single-week viewer at /n2/research/:weekIso.
 *
 * Renders the full weekly report htmlBody (sanitized with DOMPurify) from
 * notches-weekly-reports-prod, wrapped in N2 branding. The "Beyond the
 * letter" ladder lives in a sticky right sidebar (desktop) or a compact
 * banner above the report (mobile / narrow) so it never fights the reading.
 *
 * Data source is the SAME notches-weekly-reports-prod DDB the app's
 * /notches/reports page reads. Same JWT auth + entitlement plumbing.
 * Anonymous visitors see a sign-in / register gate per rebrand plan §13.
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth } from '../contexts/AuthContext';
import { N2Frame } from './N2Frame';
import { palette } from './theme';
import { PrimaryButton, SecondaryButton } from './N2Primitives';
import {
  weeklyReportsService, WeeklyReportFull, ReportAudience,
} from '../services/weeklyReportsService';

// The email HTML is server-composed and includes AI-generated commentary —
// soft prompt-injection surface. Restrict to the tags the reports actually
// use, and STRIP all presentation attributes so the N2 prose CSS below owns
// typography + color end-to-end (Fraunces headings, N2 sans body, gold
// accents). No inline styles from the email version bleed through.
const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'a', 'b', 'blockquote', 'br', 'caption', 'code', 'div', 'em', 'figure',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
    'pre', 's', 'small', 'span', 'strong', 'sub', 'sup', 'table', 'tbody',
    'td', 'tfoot', 'th', 'thead', 'tr', 'u', 'ul',
  ],
  // Semantic + link/image + style. 'style' is post-filtered below to keep
  // ONLY `color:` (semantic regime tags: red = cascade risk, green = calm,
  // yellow = tipping point, orange = downward drift). All other inline
  // styling from the email is dropped so the N2 house typography wins.
  ALLOWED_ATTR: ['alt', 'colspan', 'href', 'rowspan', 'src', 'style', 'target', 'title'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  ALLOW_DATA_ATTR: false,
};

/**
 * Strip the report's own top header (subscriber tag + "Weekly recap and
 * look-ahead" + week-of line) because the N2 shell already provides that
 * chrome. Everything else in the report body stays intact.
 */
function stripDuplicateHeader(html: string): string {
  // The report opens with:
  //   <div style="border-bottom:2px solid #eee;padding-bottom:12px;margin-bottom:8px">...</div>
  // followed by chart + sections. Match that first div and drop it.
  return html.replace(
    /<div\s+style="border-bottom:2px solid #eee[^"]*"[^>]*>[\s\S]*?<\/div>\s*<\/div>/i,
    ''
  );
}

/**
 * After DOMPurify sanitize, walk every `style="..."` attribute and reduce
 * it to just the semantic-color properties (color, background/-color,
 * border-left, border-*-color). Everything else — font-*, margin, padding,
 * text-align, width, height — is dropped so the N2 house CSS owns typography.
 *
 * Preserves the report's semantic coding:
 *   - text colors on regime tags + weekly change %
 *   - border-left on per-symbol status boxes ("SPX: ends the week in ...")
 *   - background/color on colored callout blocks
 */
function keepOnlyInlineColor(html: string): string {
  const KEEP_PROPS = new Set([
    'color',
    'background', 'background-color',
    'border-left', 'border-right', 'border-top', 'border-bottom',
    'border-left-color', 'border-right-color', 'border-top-color', 'border-bottom-color',
  ]);
  return html.replace(/\sstyle="([^"]*)"/gi, (_, styleContent: string) => {
    const kept: string[] = [];
    for (const rule of styleContent.split(';')) {
      const idx = rule.indexOf(':');
      if (idx < 0) continue;
      const prop = rule.slice(0, idx).trim().toLowerCase();
      const val = rule.slice(idx + 1).trim();
      if (!prop || !val) continue;
      if (KEEP_PROPS.has(prop)) {
        kept.push(`${prop}: ${val}`);
      }
    }
    return kept.length ? ` style="${kept.join('; ')}"` : '';
  });
}

// ---- Root --------------------------------------------------------------

export const N2ResearchLetterPage: React.FC = () => {
  const { weekIso } = useParams<{ weekIso: string }>();
  const isValidWeek = !!weekIso && /^\d{4}-W\d{2}$/.test(weekIso);

  return (
    <N2Frame page={`research-letter-${weekIso || 'invalid'}`}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 32px 40px' }}>
        <BackLink />
        {isValidWeek
          ? <WeeklyReportLoader weekIso={weekIso as string} />
          : <InvalidWeek raw={weekIso} />}
      </div>
    </N2Frame>
  );
};

// ---- Loader / gates ----------------------------------------------------

const WeeklyReportLoader: React.FC<{ weekIso: string }> = ({ weekIso }) => {
  const { token } = useAuth();
  const [report, setReport] = useState<WeeklyReportFull | null>(null);
  const [audience, setAudience] = useState<ReportAudience | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true); setError(null); setNotFound(false);
    setUnauthorized(false); setReport(null); setAudience(null);

    // Pick the best audience the caller is entitled to.
    weeklyReportsService.list(token)
      .then((listRes) => {
        if (cancelled) return null;
        const chosen: ReportAudience | null =
          listRes.audiences.includes('notches') ? 'notches'
          : listRes.audiences.includes('bb') ? 'bb'
          : null;
        if (!chosen) { setUnauthorized(true); return null; }
        setAudience(chosen);
        return weeklyReportsService.get(token, chosen, weekIso);
      })
      .then((r) => { if (r && !cancelled) setReport(r); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401 || status === 403) setUnauthorized(true);
        else if (status === 404) setNotFound(true);
        else setError(e?.response?.data?.error || e?.message || 'Failed to load');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, weekIso]);

  if (!token) return <SignInGate weekIso={weekIso} />;
  if (loading) return <LoadingBlock />;
  if (unauthorized) return <SubscribeGate weekIso={weekIso} />;
  if (notFound) return <NotFoundBlock weekIso={weekIso} />;
  if (error) return <ErrorBlock message={error} />;
  if (!report) return null;
  return <LetterView report={report} audience={audience} />;
};

const LoadingBlock: React.FC = () => (
  <div style={{
    marginTop: 32, padding: '48px 0',
    fontFamily: 'var(--sans)', fontSize: 14, color: palette.ink3,
    textAlign: 'center',
  }}>Loading the letter…</div>
);

const SignInGate: React.FC<{ weekIso: string }> = ({ weekIso }) => (
  <GatePanel
    eyebrow="Notches Weekly · sign in to read"
    heading="This letter is available to Notches subscribers."
    body="Notches Weekly is free — free forever — but delivery requires a verified account (SES compliance). Register once and every future letter is available here and in your inbox."
    primary={<PrimaryButton to={`/register?source=notches-weekly&next=/n2/research/${weekIso}`}>Create your free account</PrimaryButton>}
    secondary={<SecondaryButton to={`/login?next=/n2/research/${weekIso}`}>Sign in</SecondaryButton>}
  />
);

const SubscribeGate: React.FC<{ weekIso: string }> = ({ weekIso }) => (
  <GatePanel
    eyebrow="Notches Weekly · subscription required"
    heading="This letter is available to Notches subscribers."
    body="Your account isn't currently entitled to Notches Weekly. It's free — enable it in your Profile and every future letter lands in your inbox."
    primary={<PrimaryButton to="/profile">Enable in Profile</PrimaryButton>}
    secondary={<SecondaryButton to="/n2/research">Back to the archive</SecondaryButton>}
  />
);

const NotFoundBlock: React.FC<{ weekIso: string }> = ({ weekIso }) => (
  <div style={{
    marginTop: 40, padding: '48px 40px',
    border: `1px dashed ${palette.line}`, background: palette.paper,
    borderRadius: 4, textAlign: 'center',
  }}>
    <div style={{
      fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22,
      color: palette.ink2, marginBottom: 12,
    }}>No letter published for {weekIso}.</div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 14, color: palette.ink3,
      maxWidth: '48ch', margin: '0 auto 20px', lineHeight: 1.6,
    }}>Either the week identifier is wrong, or that week's letter hasn't shipped yet.</div>
    <SecondaryButton to="/n2/research">Back to the archive</SecondaryButton>
  </div>
);

const ErrorBlock: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    marginTop: 32, padding: '24px 28px', borderRadius: 3,
    background: `${palette.negative}10`, border: `1px solid ${palette.negative}40`,
    fontFamily: 'var(--sans)', fontSize: 13, color: palette.negative,
  }}>
    Couldn't load the letter. Try again in a minute.
    <div style={{ marginTop: 6, color: palette.ink3, fontSize: 11 }}>{message}</div>
  </div>
);

const InvalidWeek: React.FC<{ raw: string | undefined }> = ({ raw }) => (
  <div style={{
    marginTop: 40, padding: '48px 40px',
    border: `1px dashed ${palette.line}`, background: palette.paper,
    borderRadius: 4, textAlign: 'center',
  }}>
    <div style={{
      fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 22,
      color: palette.ink2, marginBottom: 12,
    }}>That's not a valid week identifier.</div>
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 13, color: palette.ink3,
      marginBottom: 20,
    }}>Expected format: <code style={{
      background: palette.paper2, padding: '2px 6px', borderRadius: 2,
      fontFamily: 'var(--mono)',
    }}>YYYY-Www</code> (e.g. 2026-W28). Got: <code style={{
      background: palette.paper2, padding: '2px 6px', borderRadius: 2,
      fontFamily: 'var(--mono)',
    }}>{raw || '(empty)'}</code></div>
    <SecondaryButton to="/n2/research">Back to the archive</SecondaryButton>
  </div>
);

// ---- LetterView: header + 2-column layout (report + sidebar) -----------

const LetterView: React.FC<{ report: WeeklyReportFull; audience: ReportAudience | null }> = ({ report, audience }) => (
  <article style={{ marginTop: 16 }}>
    <LetterHeader report={report} audience={audience} />
    <div className="n2-letter-grid" style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) 260px',
      gap: 40,
      marginTop: 32,
    }}>
      <ReportBody html={report.htmlBody} />
      <Sidebar weekIso={report.weekIso} generatedAt={report.generatedAt} />
    </div>
    <LetterFooter />
    <style>{responsiveCss}</style>
  </article>
);

const LetterHeader: React.FC<{ report: WeeklyReportFull; audience: ReportAudience | null }> = ({ report, audience }) => (
  <header style={{ paddingBottom: 20, borderBottom: `1px solid ${palette.line}` }}>
    <div style={{
      display: 'flex', gap: 14, alignItems: 'baseline',
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase', color: palette.ink3,
      marginBottom: 14,
    }}>
      <span style={{
        color: palette.goldDeep,
        fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15,
        letterSpacing: '0.02em', textTransform: 'none',
      }}>
        {audience === 'bb' ? 'The Boring Bucket' : 'Notches Weekly'}
      </span>
      <span>·</span>
      <span>{report.weekIso}</span>
    </div>
    <h1 style={{
      fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 38,
      lineHeight: 1.1, letterSpacing: '-0.015em', color: palette.ink,
      margin: 0,
      textWrap: 'balance' as React.CSSProperties['textWrap'],
    }}>Week of {report.weekLabel}</h1>
  </header>
);

const ReportBody: React.FC<{ html: string }> = ({ html }) => {
  const cleaned = stripDuplicateHeader(html);
  const safe = DOMPurify.sanitize(cleaned, SANITIZE_CONFIG) as string;
  const colorsOnly = keepOnlyInlineColor(safe);
  return (
    <div className="n2-report-container">
      <div dangerouslySetInnerHTML={{ __html: colorsOnly }} />
      <style>{reportContainerCss}</style>
    </div>
  );
};

// N2 prose treatment for the report body — Fraunces serif headings, N2
// sans body, gold accents, cream backgrounds. Overrides the email HTML's
// original inline styling (already stripped by DOMPurify ALLOWED_ATTR).
const reportContainerCss = `
.n2-report-container {
  font-family: var(--sans);
  font-size: 15px;
  line-height: 1.65;
  color: var(--ink);
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 4px;
  padding: 32px 40px;
}
.n2-report-container > div,
.n2-report-container > div > div {
  padding: 0;
  margin: 0;
  max-width: none;
}

/* Headings — Fraunces, editorial */
.n2-report-container h1 {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 32px;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: var(--ink);
  margin: 32px 0 12px;
}
.n2-report-container h2 {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 26px;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 36px 0 14px;
}
.n2-report-container h3 {
  font-family: var(--sans);
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--gold-deep);
  margin: 36px 0 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--line);
}
.n2-report-container h3:first-child { margin-top: 0; }
.n2-report-container h4 {
  font-family: var(--serif);
  font-weight: 400;
  font-size: 18px;
  line-height: 1.25;
  color: var(--ink);
  margin: 24px 0 10px;
}

/* Body copy */
.n2-report-container p {
  margin: 0 0 14px;
  color: var(--ink);
}
.n2-report-container strong { font-weight: 600; color: var(--ink); }
.n2-report-container em { font-style: italic; color: var(--gold-deep); }
.n2-report-container a {
  color: var(--gold-deep);
  text-decoration: none;
  border-bottom: 1px solid rgba(139, 111, 42, 0.4);
}
.n2-report-container a:hover { border-bottom-color: var(--gold-deep); }

/* Lists */
.n2-report-container ul, .n2-report-container ol {
  padding-left: 22px;
  margin: 0 0 16px;
}
.n2-report-container li {
  margin: 6px 0;
  color: var(--ink);
  line-height: 1.6;
}

/* Tables — plain rows, gold-tinged header, tabular numerics */
.n2-report-container table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  margin: 16px 0 24px;
  font-family: var(--sans);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
.n2-report-container thead {
  background: var(--paper-2);
}
.n2-report-container th {
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-3);
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
}
.n2-report-container td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  vertical-align: top;
}
.n2-report-container tbody tr:last-child td { border-bottom: none; }

/* Code + mono */
.n2-report-container code {
  font-family: var(--mono);
  font-size: 13px;
  background: var(--paper-2);
  padding: 1px 6px;
  border-radius: 2px;
  color: var(--ink);
}

/* Chart image */
.n2-report-container img {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 8px auto 20px;
  border: 1px solid var(--line);
  border-radius: 3px;
  background: var(--paper);
}

/* Separators */
.n2-report-container hr {
  border: 0;
  border-top: 1px solid var(--line);
  margin: 28px 0;
}

/* Small text */
.n2-report-container small {
  font-size: 12px;
  color: var(--ink-3);
}
`;

// ---- Sidebar: Beyond the letter ---------------------------------------

const Sidebar: React.FC<{ weekIso: string; generatedAt: string }> = ({ weekIso, generatedAt }) => {
  const utm = `utm_source=notches-weekly&utm_campaign=${encodeURIComponent(weekIso)}&utm_content=`;
  return (
    <aside className="n2-letter-sidebar" style={{
      position: 'sticky', top: 24, alignSelf: 'start',
      background: palette.paper2, border: `1px solid ${palette.line}`,
      borderRadius: 4, padding: '24px 22px',
    }}>
      <div style={{
        fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 700,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: palette.goldDeep, marginBottom: 16,
      }}>Beyond the letter</div>

      <SidebarItem
        title="Notches Desk"
        body="The live research app. Every notch this letter recaps — GEX flips, COT shifts, IV moves, AVWAP breaks — publishes as it happens, per market."
        href={`https://www.wealthfinancialsystems.com/register?${utm}desk`}
        cta="Access Desk →"
      />

      <SidebarItem
        title="Notches Research"
        body="Paid weekly letter with per-market drilldowns and full model outputs. Launching soon."
        href={`https://www.wealthfinancialsystems.com/register?${utm}research-waitlist`}
        cta="Join waitlist →"
      />

      <SidebarItem
        title="Notches Managed"
        body="For RIAs and books over $200K, we run these reads systematically."
        href={`mailto:bobby@bv-holdings.com?subject=${encodeURIComponent('Notches Managed')}`}
        cta="Contact →"
        last
      />

      <div style={{
        marginTop: 20, paddingTop: 16, borderTop: `1px solid ${palette.line}`,
        fontFamily: 'var(--sans)', fontSize: 11, color: palette.ink3, lineHeight: 1.5,
      }}>
        Published {new Date(generatedAt).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        })}
      </div>
    </aside>
  );
};

const SidebarItem: React.FC<{
  title: string; body: string; href: string; cta: string; last?: boolean;
}> = ({ title, body, href, cta, last }) => (
  <div style={{
    paddingBottom: last ? 0 : 16,
    marginBottom: last ? 0 : 16,
    borderBottom: last ? 'none' : `1px solid ${palette.line}`,
  }}>
    <div style={{
      fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 400,
      color: palette.ink, marginBottom: 6, letterSpacing: '-0.005em',
    }}>{title}</div>
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 13, lineHeight: 1.55,
      color: palette.ink2, margin: '0 0 10px',
    }}>{body}</p>
    <a href={href} style={{
      fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 600,
      color: palette.goldDeep, textDecoration: 'none',
      borderBottom: `1.5px solid ${palette.gold}`, paddingBottom: 1,
    }}>{cta}</a>
  </div>
);

// ---- Footer -----------------------------------------------------------

const LetterFooter: React.FC = () => (
  <footer style={{
    marginTop: 56, paddingTop: 24, borderTop: `1px solid ${palette.line}`,
    fontFamily: 'var(--sans)', fontSize: 12, color: palette.ink3,
    lineHeight: 1.6, fontStyle: 'italic',
  }}>
    Notches Weekly is a research letter. Not investment advice. WFS is not a
    registered investment adviser. Consult a registered investment adviser
    before making investment decisions.
  </footer>
);

const BackLink: React.FC = () => (
  <Link to="/n2/research" style={{
    fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600,
    letterSpacing: '0.14em', textTransform: 'uppercase',
    color: palette.ink3, textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    marginBottom: 24,
  }}>← Archive</Link>
);

// Below ~900px the sidebar becomes a banner above the report.
const responsiveCss = `
@media (max-width: 900px) {
  .n2-letter-grid {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 24px !important;
  }
  .n2-letter-sidebar {
    position: static !important;
    order: -1;
  }
}
`;

// ---- Gate panel -------------------------------------------------------

const GatePanel: React.FC<{
  eyebrow: string;
  heading: string;
  body: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
}> = ({ eyebrow, heading, body, primary, secondary }) => (
  <div style={{
    marginTop: 24, padding: '48px 44px',
    background: palette.paper2, border: `1.5px solid ${palette.goldDeep}`,
    borderRadius: 4, position: 'relative',
  }}>
    <div style={{
      position: 'absolute', top: -1, left: 44, width: 40, height: 3,
      background: palette.gold,
    }} />
    <div style={{
      fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: palette.goldDeep, marginBottom: 16,
    }}>{eyebrow}</div>
    <h3 style={{
      fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 28,
      lineHeight: 1.2, color: palette.ink, letterSpacing: '-0.01em',
      margin: '0 0 16px',
    }}>{heading}</h3>
    <p style={{
      fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.65,
      color: palette.ink2, margin: '0 0 28px', maxWidth: '54ch',
    }}>{body}</p>
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {primary}
      {secondary}
    </div>
  </div>
);

export default N2ResearchLetterPage;
